import type { Schemas } from "../../lib/api/client";
import { cn } from "../../lib/cn";
import { useLogoSource } from "./logo-source";

/**
 * The business's logo on a light plate, so a dark logo stays visible in the
 * dark appearance (Musweu, 16 October 2026), or the BurgePOS mark without one.
 * The business's name is drawn beside it as text, so the image itself is
 * decorative unless no name was recorded. While the session or the logo is
 * still loading, a blank plate of the same size holds its place, so nothing
 * flashes from the BurgePOS mark to the business's.
 */
export function BusinessLogo({
  branding,
  pending = false,
  small = false,
}: {
  branding: Schemas["Branding"] | null | undefined;
  pending?: boolean;
  small?: boolean;
}) {
  const source = useLogoSource(branding?.logo);
  if (pending || source === undefined) {
    return <span aria-hidden="true" className={cn("shrink-0 rounded-full bg-surface-muted", small ? "size-9" : "size-11")} />;
  }
  if (source) {
    return (
      <span className={cn("grid shrink-0 place-items-center rounded-control bg-logo-plate px-2", small ? "h-9 max-w-28" : "h-11 max-w-32")}>
        <img
          alt={branding?.business_name ? "" : "Your business's logo"}
          className={cn("w-auto object-contain", small ? "max-h-7" : "max-h-9")}
          src={source}
        />
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className={cn(
        "grid shrink-0 place-items-center rounded-full bg-accent text-sm font-extrabold text-on-accent",
        small ? "size-9" : "size-11",
      )}
    >
      BP
    </span>
  );
}
